// Median-cut palette extraction. Produces N representative colours from an
// image. Used by image-to-palette auto-quantization.

import type { RGB } from './color';

export function medianCut(rgba: Uint8ClampedArray, count: number): RGB[] {
  const target = Math.max(2, Math.min(64, Math.floor(count)));
  // Collect non-transparent pixels.
  const pixels: number[][] = [];
  for (let i = 0; i < rgba.length; i += 4) {
    if ((rgba[i + 3] as number) < 8) continue;
    pixels.push([rgba[i] as number, rgba[i + 1] as number, rgba[i + 2] as number]);
  }
  if (pixels.length === 0) return [[0, 0, 0]];

  // Recursively split buckets until we have `target` of them.
  let buckets: number[][][] = [pixels];
  while (buckets.length < target) {
    const idx = pickWidestBucket(buckets);
    if (idx < 0) break;
    const bucket = buckets[idx]!;
    if (bucket.length < 2) break;
    const [a, b] = splitBucket(bucket);
    buckets.splice(idx, 1, a, b);
  }

  return buckets.map((b) => averageColor(b));
}

function pickWidestBucket(buckets: number[][][]): number {
  let best = -1;
  let bestRange = -1;
  for (let i = 0; i < buckets.length; i++) {
    const b = buckets[i]!;
    if (b.length < 2) continue;
    const range = colorRange(b);
    if (range > bestRange) {
      bestRange = range;
      best = i;
    }
  }
  return best;
}

function colorRange(bucket: number[][]): number {
  let rMin = 255, gMin = 255, bMin = 255;
  let rMax = 0, gMax = 0, bMax = 0;
  for (const p of bucket) {
    if (p[0]! < rMin) rMin = p[0]!;
    if (p[1]! < gMin) gMin = p[1]!;
    if (p[2]! < bMin) bMin = p[2]!;
    if (p[0]! > rMax) rMax = p[0]!;
    if (p[1]! > gMax) gMax = p[1]!;
    if (p[2]! > bMax) bMax = p[2]!;
  }
  return Math.max(rMax - rMin, gMax - gMin, bMax - bMin);
}

function splitBucket(bucket: number[][]): [number[][], number[][]] {
  let rMin = 255, gMin = 255, bMin = 255;
  let rMax = 0, gMax = 0, bMax = 0;
  for (const p of bucket) {
    if (p[0]! < rMin) rMin = p[0]!;
    if (p[1]! < gMin) gMin = p[1]!;
    if (p[2]! < bMin) bMin = p[2]!;
    if (p[0]! > rMax) rMax = p[0]!;
    if (p[1]! > gMax) gMax = p[1]!;
    if (p[2]! > bMax) bMax = p[2]!;
  }
  const rR = rMax - rMin, gR = gMax - gMin, bR = bMax - bMin;
  const channel = rR >= gR && rR >= bR ? 0 : (gR >= bR ? 1 : 2);
  bucket.sort((p1, p2) => (p1[channel] as number) - (p2[channel] as number));
  const mid = Math.floor(bucket.length / 2);
  return [bucket.slice(0, mid), bucket.slice(mid)];
}

function averageColor(bucket: number[][]): RGB {
  let r = 0, g = 0, b = 0;
  for (const p of bucket) {
    r += p[0] as number;
    g += p[1] as number;
    b += p[2] as number;
  }
  const n = bucket.length || 1;
  return [Math.round(r / n), Math.round(g / n), Math.round(b / n)];
}

export function rgbToHex(rgb: RGB): string {
  return '#' + rgb.map((c) => Math.max(0, Math.min(255, c | 0)).toString(16).padStart(2, '0')).join('');
}
